package dev.lingvo.site.state

import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.Session
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException

/** Exact selected-workspace decisions for server-protected settings resources and actions. */
internal data class WorkspaceActionAuthority(
    val role: Role?,
) {
    val canReadBilling: Boolean get() = role?.atLeast(Role.ADMIN) == true
    val canManageProviders: Boolean get() = role?.atLeast(Role.ADMIN) == true
    val canManageAgents: Boolean get() = role?.atLeast(Role.ADMIN) == true
    val canUseOwnerBilling: Boolean get() = role == Role.OWNER

    /**
     * Whether this membership may spend the workspace's machine-translation credits.
     *
     * The same bar as [canManageProviders], deliberately named apart from it. Connecting a provider
     * and spending on it are one privilege level and the server gates both on owner-or-admin, but a
     * pre-translate run captured under `PROVIDERS` reads at the call site as a copy-paste from the
     * settings page rather than as a decision. Naming the spend is what makes the claim legible.
     */
    val canSpendCredits: Boolean get() = role?.atLeast(Role.ADMIN) == true
    val canCreateSample: Boolean get() = role?.atLeast(Role.ADMIN) == true

    companion object {
        fun resolve(context: WorkspaceRequestContext?): WorkspaceActionAuthority =
            WorkspaceActionAuthority(context?.role)
    }
}

/** Named server privilege used to capture a request/action against one exact membership snapshot. */
internal enum class WorkspacePrivilege {
    BILLING_READ,
    OWNER_BILLING,
    PROVIDERS,
    AGENTS,
    MT_SPEND,
    SAMPLE_PROJECT,
}

/**
 * A pure claim for one selected-workspace request or action.
 *
 * Pages capture this before transport and compare it again after suspension. The role is retained in
 * addition to [WorkspaceRequestKey]: accepted session refreshes already change the key, while the
 * explicit role comparison keeps the invariant visible and makes fabricated/stale contexts fail
 * closed as well.
 */
internal class WorkspaceActionClaim private constructor(
    val key: WorkspaceRequestKey,
    val workspaceSlug: String,
    private val role: Role,
    private val privilege: WorkspacePrivilege,
) {
    val subject = WorkspaceAuthoritySubject(key, role)

    fun remainsAuthorized(current: WorkspaceRequestContext?): Boolean =
        current?.key == key &&
            current.workspace.slug == workspaceSlug &&
            current.role == role &&
            (privilege != WorkspacePrivilege.SAMPLE_PROJECT || current.decision(WorkspaceRequestKind.SAMPLE_PROJECT).allowed) &&
            WorkspaceActionAuthority.resolve(current).allows(privilege)

    companion object {
        fun capture(
            context: WorkspaceRequestContext?,
            privilege: WorkspacePrivilege,
        ): WorkspaceActionClaim? {
            val exact = context ?: return null
            if (privilege == WorkspacePrivilege.SAMPLE_PROJECT && !exact.decision(WorkspaceRequestKind.SAMPLE_PROJECT).allowed) return null
            val authority = WorkspaceActionAuthority.resolve(exact)
            if (!authority.allows(privilege)) return null
            return WorkspaceActionClaim(
                key = exact.key,
                workspaceSlug = exact.workspace.slug,
                role = exact.role,
                privilege = privilege,
            )
        }
    }
}

/**
 * Exact selected-workspace transport boundary for privileged settings resources.
 *
 * The claim is checked both before the injected request and after it completes. This makes a direct
 * viewer URL issue zero requests and prevents a late response from a demoted/switched membership
 * becoming a value that an async store can publish into the active surface. The transport and live
 * context are injected, so the authority decision remains deterministic and independently testable.
 */
internal suspend fun <T> requestWorkspaceResource(
    context: WorkspaceRequestContext?,
    privilege: WorkspacePrivilege,
    currentContext: () -> WorkspaceRequestContext?,
    transport: suspend (workspaceSlug: String) -> T,
): T {
    val claim = WorkspaceActionClaim.capture(context, privilege)
        ?: error("This workspace role cannot request the protected resource.")
    return requestWorkspaceAction(claim, currentContext, transport)
}

/** Executes one suspension under an exact selected-workspace action claim. */
internal suspend fun <T> requestWorkspaceAction(
    claim: WorkspaceActionClaim,
    currentContext: () -> WorkspaceRequestContext?,
    transport: suspend (workspaceSlug: String) -> T,
): T {
    if (!claim.remainsAuthorized(currentContext())) {
        authorityChanged("The workspace authority changed before the request could start.")
    }
    val value = try {
        transport(claim.workspaceSlug)
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        if (!claim.remainsAuthorized(currentContext())) {
            authorityChanged("The workspace authority changed while the request was failing.")
        }
        throw failure
    }
    if (!claim.remainsAuthorized(currentContext())) {
        authorityChanged("The workspace authority changed before the response could be published.")
    }
    return value
}

/**
 * Runs a UI action without exposing click-to-coroutine or late-publication races to every page.
 *
 * Authority loss is a quiet retirement, while an actual transport cancellation keeps its exact
 * cancellation identity. Busy and result callbacks only mutate the still-current workspace state.
 */
internal suspend fun <T> runWorkspaceAction(
    claim: WorkspaceActionClaim,
    currentContext: () -> WorkspaceRequestContext?,
    setBusy: (Boolean) -> Unit,
    transport: suspend (workspaceSlug: String) -> T,
    onSuccess: (T) -> Unit,
    onFailure: (Throwable) -> Unit,
) {
    if (!claim.remainsAuthorized(currentContext())) return
    setBusy(true)
    try {
        val value = requestWorkspaceAction(claim, currentContext, transport)
        onSuccess(value)
    } catch (_: AuthorityChangedCancellationException) {
        // The subject changed while this child action was suspended. Its UI state is retired.
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        if (claim.remainsAuthorized(currentContext())) onFailure(failure)
    } finally {
        if (claim.remainsAuthorized(currentContext())) setBusy(false)
    }
}

private fun WorkspaceActionAuthority.allows(privilege: WorkspacePrivilege): Boolean = when (privilege) {
    WorkspacePrivilege.BILLING_READ -> canReadBilling
    WorkspacePrivilege.OWNER_BILLING -> canUseOwnerBilling
    WorkspacePrivilege.PROVIDERS -> canManageProviders
    WorkspacePrivilege.AGENTS -> canManageAgents
    WorkspacePrivilege.MT_SPEND -> canSpendCredits
    WorkspacePrivilege.SAMPLE_PROJECT -> canCreateSample
}

/** Exact verified-project decisions. This model never borrows selected or strongest workspace role. */
internal data class ProjectAuthority(
    val role: Role?,
) {
    val canReadProject: Boolean get() = role != null
    val canWriteTranslations: Boolean get() = role != null && role != Role.VIEWER
    val canDecideProposals: Boolean get() = role != null && role != Role.VIEWER
    val canEditStructure: Boolean get() = role?.atLeast(Role.ADMIN) == true
    val canExemptQa: Boolean get() = role?.atLeast(Role.ADMIN) == true
    val canDeleteProject: Boolean get() = role == Role.OWNER

    /**
     * Whether this project role may spend the workspace's machine-translation credits.
     *
     * The [WorkspaceActionAuthority.canSpendCredits] rule, read from the project side: the server
     * gates a pre-translate run on owner-or-admin, which is the same bar [canEditStructure] happens
     * to apply. Same bar, different decision — a run costs money and adds a file does not — and the
     * two were being captured under one name, so a spend on a project route read at its call site as
     * "this person may edit the project's structure". Naming the spend is what makes the claim
     * legible, and it is what lets the bar move for one of them later without silently moving for
     * the other.
     *
     * The browser's half of the answer is a *floor*, not the whole of it. The token half —
     * `mt:spend` on the credential — is invisible here, which is why the surfaces that draw an
     * estimate ask the server for `EditorCapabilitiesDto.canSpendCredits` rather than inferring it
     * from a role.
     */
    val canSpendCredits: Boolean get() = role?.atLeast(Role.ADMIN) == true

    companion object {
        fun resolve(
            project: ProjectDetailDto?,
            memberships: List<WorkspaceDto>,
        ): ProjectAuthority {
            val role = project?.let { detail ->
                detail.orgSlug
                    .takeIf(String::isNotBlank)
                    ?.let { slug -> memberships.firstOrNull { it.slug == slug }?.role }
                    ?: memberships.firstOrNull { it.id == detail.orgId }?.role
            }
            return ProjectAuthority(role)
        }
    }
}

internal enum class ProjectPrivilege {
    READ,
    STRUCTURE,
    PROPOSAL_DECISION,
    QA_MANAGEMENT,

    /** Starting a machine-translation run from a project route. See [ProjectAuthority.canSpendCredits]. */
    SPEND,
}

/** Stable identity for remembered project action state and verified secondary reads. */
internal data class ProjectActionKey(
    val workspace: WorkspaceRequestKey,
    val projectId: String,
    val routeSlug: String,
)

/** One exact verified-project role subject for remembered UI state and protected reads. */
internal data class ProjectAuthoritySubject(
    val action: ProjectActionKey,
    val role: Role,
)

internal fun VerifiedProjectRoute.actionKey(): ProjectActionKey = ProjectActionKey(
    workspace = workspace.key,
    projectId = project.id,
    routeSlug = project.slug,
)

internal fun VerifiedProjectRoute.authoritySubject(
    memberships: List<WorkspaceDto>,
): ProjectAuthoritySubject? = ProjectAuthority.resolve(project, memberships).role?.let { role ->
    ProjectAuthoritySubject(actionKey(), role)
}

/**
 * Returns the cached verified route only when it is also the browser's live project route now.
 *
 * [ProjectRouteContext.current] is deliberately a cache lookup and accepts its caller's slug. That
 * makes it useful for rendering an already-gated route, but insufficient for a click or suspended
 * action: the previous project's cache entry remains available after same-session navigation. This
 * boundary adds the live pathname/session identity before any Task 3 action consumes that cache.
 */
internal fun liveProjectRoute(expectedSlug: String): VerifiedProjectRoute? {
    val browserClaim = projectRouteClaim(Session.requestIdentity, window.location.pathname)
    val cachedRoute = browserClaim
        ?.takeIf { it.routeSlug == expectedSlug }
        ?.let { ProjectRouteContext.current(it.routeSlug) }
    return liveProjectRoute(expectedSlug, browserClaim, cachedRoute)
}

/** Pure seam for route/session adversarial tests; production uses [liveProjectRoute]. */
internal fun liveProjectRoute(
    expectedSlug: String,
    browserClaim: ProjectRouteClaim?,
    cachedRoute: VerifiedProjectRoute?,
): VerifiedProjectRoute? {
    val claim = browserClaim ?: return null
    val route = cachedRoute ?: return null
    return route.takeIf {
        expectedSlug.isNotBlank() &&
            claim.routeSlug == expectedSlug &&
            route.project.slug == expectedSlug &&
            route.workspace.session == claim.session &&
            route.workspace.acceptsProject(route.project)
    }
}

/** One mutation claim bound to the verified route, authenticated generation, and exact project role. */
internal class ProjectActionClaim private constructor(
    val routeSlug: String,
    val workspaceKey: WorkspaceRequestKey,
    private val projectId: String,
    private val projectOrgId: String,
    private val projectOrgSlug: String,
    private val role: Role,
    private val privilege: ProjectPrivilege,
) {
    val subject = ProjectAuthoritySubject(
        action = ProjectActionKey(workspaceKey, projectId, routeSlug),
        role = role,
    )

    fun remainsAuthorized(
        current: VerifiedProjectRoute?,
        memberships: List<WorkspaceDto>,
    ): Boolean {
        val route = current ?: return false
        val authority = ProjectAuthority.resolve(route.project, memberships)
        return route.workspace.key == workspaceKey &&
            route.project.id == projectId &&
            route.project.slug == routeSlug &&
            route.project.orgId == projectOrgId &&
            route.project.orgSlug == projectOrgSlug &&
            authority.role == role &&
            authority.allows(privilege)
    }

    companion object {
        fun capture(
            route: VerifiedProjectRoute?,
            memberships: List<WorkspaceDto>,
            privilege: ProjectPrivilege,
        ): ProjectActionClaim? {
            val verified = route ?: return null
            val authority = ProjectAuthority.resolve(verified.project, memberships)
            val exactRole = authority.role ?: return null
            if (!authority.allows(privilege)) return null
            return ProjectActionClaim(
                routeSlug = verified.project.slug,
                workspaceKey = verified.workspace.key,
                projectId = verified.project.id,
                projectOrgId = verified.project.orgId,
                projectOrgSlug = verified.project.orgSlug,
                role = exactRole,
                privilege = privilege,
            )
        }
    }
}

/**
 * Executes one suspension under an already captured project-action claim.
 *
 * Call this at coroutine entry and again for every later request in a batch. Each invocation checks
 * the live browser route and current membership both before transport and after suspension, so a
 * route/session/role change stops before the next request and returns no publishable value.
 */
internal suspend fun <T> requestProjectAction(
    claim: ProjectActionClaim,
    currentRoute: () -> VerifiedProjectRoute?,
    currentMemberships: () -> List<WorkspaceDto>,
    transport: suspend () -> T,
): T {
    if (!claim.remainsAuthorized(currentRoute(), currentMemberships())) {
        authorityChanged("The project authority changed before the request could start.")
    }
    val value = try {
        transport()
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        if (!claim.remainsAuthorized(currentRoute(), currentMemberships())) {
            authorityChanged("The project authority changed while the request was failing.")
        }
        throw failure
    }
    if (!claim.remainsAuthorized(currentRoute(), currentMemberships())) {
        authorityChanged("The project authority changed before the response could be published.")
    }
    return value
}

/** Runs a project UI action while retiring any result whose live route or exact role changed. */
internal suspend fun <T> runProjectAction(
    claim: ProjectActionClaim,
    currentRoute: () -> VerifiedProjectRoute?,
    currentMemberships: () -> List<WorkspaceDto>,
    setBusy: (Boolean) -> Unit,
    transport: suspend () -> T,
    onSuccess: (T) -> Unit,
    onFailure: (Throwable) -> Unit,
) {
    if (!claim.remainsAuthorized(currentRoute(), currentMemberships())) return
    setBusy(true)
    try {
        val value = requestProjectAction(claim, currentRoute, currentMemberships, transport)
        onSuccess(value)
    } catch (_: AuthorityChangedCancellationException) {
        // The route/session/membership changed; its keyed UI state is no longer publishable.
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        if (claim.remainsAuthorized(currentRoute(), currentMemberships())) onFailure(failure)
    } finally {
        if (claim.remainsAuthorized(currentRoute(), currentMemberships())) setBusy(false)
    }
}

/** Live-route equivalent for readable project resources keyed to one verified route snapshot. */
internal suspend fun <T> requestProjectResource(
    key: ProjectActionKey,
    expectedSlug: String,
    memberships: List<WorkspaceDto> = Session.organizations,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(expectedSlug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    transport: suspend (VerifiedProjectRoute) -> T,
): T {
    val before = currentRoute()
        ?.takeIf { it.actionKey() == key }
        ?: authorityChanged("The verified project changed before the request could start.")
    val claim = ProjectActionClaim.capture(before, memberships, ProjectPrivilege.READ)
        ?: authorityChanged("The project membership changed before the request could start.")
    return requestProjectAction(
        claim = claim,
        currentRoute = {
            currentRoute()?.takeIf { it.actionKey() == key }
        },
        currentMemberships = currentMemberships,
    ) {
        transport(before)
    }
}

private class AuthorityChangedCancellationException(message: String) : CancellationException(message)

private fun authorityChanged(message: String): Nothing =
    throw AuthorityChangedCancellationException(message)

private fun ProjectAuthority.allows(privilege: ProjectPrivilege): Boolean = when (privilege) {
    ProjectPrivilege.READ -> canReadProject
    ProjectPrivilege.STRUCTURE -> canEditStructure
    ProjectPrivilege.PROPOSAL_DECISION -> canDecideProposals
    ProjectPrivilege.QA_MANAGEMENT -> canExemptQa
    ProjectPrivilege.SPEND -> canSpendCredits
}
